import {useCallback, useEffect, useMemo, useState} from "react";
import {getFlow} from "./flowSync";

// Firebase flows of every sub project of a registry project, with lookups shared by the pages that list the
// project's conversations (which span all sub projects):
//   flows: {flowUid: flow} (flow.id is the flowUid), tagNames: {tagUid: name} over every flow,
//   subProjectOf(conversation): its sub project (by subProjectId, else by flowUid),
//   flowOf(conversation): the flow it ran on (tags, success tag, phonebook, vdn manager, configuration)
export const useSubProjectFlows = (project) => {
  const subProjects = useMemo(() => project?.subProjects || [], [project?.subProjects]);
  const [flows, setFlows] = useState({});

  const flowUids = subProjects.map(item => item.flowUid).filter(Boolean).join(",");
  useEffect(() => {
    let cancelled = false;
    Promise.all(subProjects.filter(item => item.flowUid).map(item => getFlow(item)
      .then(data => [item.flowUid, {...(data || {}), id: item.flowUid}])
      .catch(e => {
        console.error("Error loading flow:", e);
        return null;
      })))
      .then(entries => !cancelled && setFlows(Object.fromEntries(entries.filter(Boolean))));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowUids]);

  const tagNames = useMemo(() => Object.fromEntries(Object.values(flows).flatMap(flow =>
    (flow?.settings?.tags || []).filter(tag => tag?.id).map(tag => [tag.id, tag.title || tag.name || tag.id])
  )), [flows]);

  const subProjectOf = useCallback((conversation) => subProjects.find(item =>
    (conversation?.subProjectId != null && String(item.id) === String(conversation.subProjectId))
    || (conversation?.flowUid && item.flowUid === conversation.flowUid)
  ), [subProjects]);

  const flowOf = useCallback((conversation) =>
    flows[conversation?.flowUid || subProjectOf(conversation)?.flowUid], [flows, subProjectOf]);

  // Tag names of a conversation; SUCCESS conversations show their flow's success tag
  const tagsOf = useCallback((conversation) => {
    const successTag = tagNames[flowOf(conversation)?.configuration?.success_tag];
    if (conversation?.status === "SUCCESS" && successTag) return [successTag];
    return (conversation?.tags || []).map(id => tagNames[id]).filter(Boolean);
  }, [tagNames, flowOf]);

  return {flows, tagNames, subProjectOf, flowOf, tagsOf};
};
